import isEmpty from "is-empty";
import React from "react";
import { Property } from "rsl-api-106/lib/types";

import Carousel from "@components/_Layout/Carousel";
import Popup from "@components/_Layout/Controls/Popup";
import { useSession } from "@hooks";
import {
  AlternateEmailOutlined,
  HomeOutlined,
  LocationOnOutlined,
  PhoneOutlined,
} from "@mui/icons-material";
import { replaceImgOnError } from "@utils/dom";
import { removeTags } from "@utils/tools";

type PropertyPopupProps = {
  btnElement: (onClick: () => void) => JSX.Element;
  property: Property;
};

const PropertyPopup: React.FC<PropertyPopupProps> = ({
  btnElement,
  property,
}) => {
  /* Hooks */
  const { getLanguageVarValue } = useSession();

  /* Render */
  return (
    <Popup btnElement={btnElement} title={property?.label ?? undefined}>
      <div data-property={property.id}>
        <div className="tw-bg-white tw-py-4 tw-px-6 tw-rounded-lg">
          <dl className="tw-divide-y tw-divide-gray-200">
            <div className="tw-px-4 tw-py-6 sm:tw-grid sm:tw-grid-cols-3 sm:tw-gap-4 sm:tw-px-0">
              <dt className="tw-text-sm tw-font-medium tw-leading-6">
                {getLanguageVarValue("LANG_neobooking_property")}
              </dt>
              <dd className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-700 sm:tw-col-span-2 sm:tw-mt-0">
                <div className="tw-flex tw-gap-x-2">
                  <div>
                    <HomeOutlined />
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-center tw-w-full  ">
                    <div className="tw-font-semibold">{property.label}</div>
                  </div>
                </div>
                <div>
                  <div className="tw-mt-4">
                    <Carousel
                      navigation
                      pagination
                      additionalClassname="tw-rounded-xl tw-h-[250px]"
                      spaceBetween={8}
                      items={[property?.imagePath].map((value) => {
                        return (
                          <img
                            onError={replaceImgOnError}
                            className="tw-rounded-xl tw-h-[250px] tw-w-full tw-object-cover"
                            src={value ?? ""}
                          />
                        );
                      })}
                    />
                  </div>
                </div>
              </dd>
            </div>
            {!isEmpty(removeTags(property?.description ?? "")) && (
              <div className="tw-px-4 tw-py-6 sm:tw-grid sm:tw-grid-cols-3 sm:tw-gap-4 sm:tw-px-0">
                <dt className="tw-text-sm tw-font-medium tw-leading-6">
                  {getLanguageVarValue("LANG_neobooking_description")}
                </dt>
                <dd className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-700 sm:tw-col-span-2 sm:tw-mt-0">
                  <div
                    dangerouslySetInnerHTML={{ __html: property.description!! }}
                  ></div>
                </dd>
              </div>
            )}
            <div className="tw-px-4 tw-py-6 sm:tw-grid sm:tw-grid-cols-3 sm:tw-gap-4 sm:tw-px-0">
              <dt className="tw-text-sm tw-font-medium tw-leading-6">
                {getLanguageVarValue("LANG_neobooking_contact")}
              </dt>
              <dd className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-700 sm:tw-col-span-2 sm:tw-mt-0">
                <div>
                  {!isEmpty(property.address?.phone1) && (
                    <div className="tw-flex tw-gap-x-2">
                      <div>
                        <PhoneOutlined />
                      </div>
                      <a
                        className="tw-text-primary-500 hover:tw-text-primary-400 tw-duration-200 tw-ease-in-out tw-transition-colors"
                        href={`tel:${property.address?.phone1}`}
                      >
                        {property.address?.phone1}
                      </a>
                    </div>
                  )}
                  {!isEmpty(property.address?.phone2) && (
                    <div className="tw-flex tw-gap-x-2">
                      <div>
                        <PhoneOutlined />
                      </div>
                      <a
                        className="tw-text-primary-500 hover:tw-text-primary-400 tw-duration-200 tw-ease-in-out tw-transition-colors"
                        href={`tel:${property.address?.phone2}`}
                      >
                        {property.address?.phone2}
                      </a>
                    </div>
                  )}
                  {!isEmpty(property.address?.fax) && (
                    <div className="tw-flex tw-gap-x-2">
                      <div>
                        <PhoneOutlined />
                      </div>
                      <a
                        className="tw-text-primary-500 hover:tw-text-primary-400 tw-duration-200 tw-ease-in-out tw-transition-colors"
                        href={`tel:${property.address?.fax}`}
                      >
                        {property.address?.fax}
                      </a>
                    </div>
                  )}
                  {!isEmpty(property.address?.mail) && (
                    <div className="tw-flex tw-gap-x-2">
                      <div>
                        <AlternateEmailOutlined />
                      </div>
                      <a
                        className="tw-text-primary-500 hover:tw-text-primary-400 tw-duration-200 tw-ease-in-out tw-transition-colors"
                        href={`mailto:${property.address?.mail}`}
                      >
                        {property.address?.mail}
                      </a>
                    </div>
                  )}
                </div>
              </dd>
            </div>
            <div className="tw-px-4 tw-py-6 sm:tw-grid sm:tw-grid-cols-3 sm:tw-gap-4 sm:tw-px-0">
              <dt className="tw-text-sm tw-font-medium tw-leading-6">
                {getLanguageVarValue("LANG_neobooking_address")}
              </dt>
              <dd className="tw-mt-1 tw-text-sm tw-leading-6 tw-text-gray-700 sm:tw-col-span-2 sm:tw-mt-0">
                <div className="tw-flex tw-gap-x-2">
                  <div>
                    <LocationOnOutlined />
                  </div>
                  <div>
                    {!isEmpty(property.address?.streetName) && (
                      <div>{property.address?.streetName}</div>
                    )}
                    {!isEmpty(property.address?.addressComplementary1) && (
                      <div>{property.address?.addressComplementary1}</div>
                    )}
                    {!isEmpty(property.address?.addressComplementary2) && (
                      <div>{property.address?.addressComplementary2}</div>
                    )}

                    <div>
                      {property.address?.city} - {property.address?.zipCode}
                    </div>
                    <div>{property.address?.addressCountry?.label}</div>
                  </div>
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </Popup>
  );
};

export default PropertyPopup;
